import { toast } from 'react-toastify';

export const EXTENSION_TO_CONTENT_TYPE = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
} as const;

type AllowedExtension = keyof typeof EXTENSION_TO_CONTENT_TYPE;

type ExtensionCategory = 'document' | 'articleImage';

export const ALLOWED_EXTENSIONS: Record<ExtensionCategory, readonly string[]> = {
  document: ['pdf', 'png', 'jpg', 'jpeg'],
  articleImage: ['png', 'jpg', 'jpeg'],
};

const getAllAllowedExtensions = (): string[] =>
  Array.from(new Set(Object.values(ALLOWED_EXTENSIONS).flat()));

export const ALLOWED_IMAGE_EXTENSIONS: readonly string[] = ALLOWED_EXTENSIONS.articleImage;

export const ALLOWED_MIME_TYPES: readonly string[] = Array.from(
  new Set(
    ALLOWED_EXTENSIONS.document.map((ext) => EXTENSION_TO_CONTENT_TYPE[ext as AllowedExtension]),
  ),
);

export const ALLOWED_IMAGE_MIME_TYPES: readonly string[] = Array.from(
  new Set(
    ALLOWED_EXTENSIONS.articleImage.map(
      (ext) => EXTENSION_TO_CONTENT_TYPE[ext as AllowedExtension],
    ),
  ),
);

/** MIME-type string for react-dropzone accept prop and native `<input accept>` (document uploads). */
export const ACCEPT_MIME_STRING = ALLOWED_MIME_TYPES.join(',');

/** MIME-type string for react-dropzone accept prop and native `<input accept>` (image-only uploads). */
export const ACCEPT_IMAGE_MIME_STRING = ALLOWED_IMAGE_MIME_TYPES.join(',');

export const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) {
    return '';
  }
  return filename.slice(lastDot + 1).toLowerCase();
};

export const getContentTypeForExtension = (extension: string): string => {
  const normalized = extension.toLowerCase();
  const contentType = EXTENSION_TO_CONTENT_TYPE[normalized as AllowedExtension];
  if (!contentType) {
    throw new Error(`Unknown file extension "${extension}". No content type mapping found.`);
  }
  return contentType;
};

/**
 * Returns `true` if the file's extension is in the allowed list.
 * Boolean counterpart to `validateFileExtension`.
 */
export const isFileExtensionAllowed = (
  filename: string,
  allowed: readonly string[] = getAllAllowedExtensions(),
): boolean => {
  const ext = getFileExtension(filename);
  return ext !== '' && allowed.includes(ext);
};

export const validateFileExtension = (
  fileName: string,
  allowed: readonly string[] = getAllAllowedExtensions(),
): void => {
  const ext = getFileExtension(fileName);
  if (!ext) {
    throw new Error(`File has no extension. Allowed extensions: ${allowed.join(', ')}`);
  }
  if (!allowed.includes(ext)) {
    throw new Error(
      `File extension "${ext}" is not allowed. Allowed extensions: ${allowed.join(', ')}`,
    );
  }
};

export const validateFileMimeType = (
  file: File,
  allowed: readonly string[] = ALLOWED_MIME_TYPES,
): void => {
  const mimeType = file.type;
  if (mimeType == null || mimeType === '' || !allowed.includes(mimeType)) {
    throw new Error(
      `MIME type "${mimeType}" is not allowed. Allowed MIME types: ${allowed.join(', ')}`,
    );
  }
};

export const validateFile = (
  file: File,
  options?: { allowedExtensions?: readonly string[]; allowedMimeTypes?: readonly string[] },
): void => {
  validateFileExtension(file.name, options?.allowedExtensions ?? getAllAllowedExtensions());
  validateFileMimeType(file, options?.allowedMimeTypes ?? ALLOWED_MIME_TYPES);
};

/**
 * Filters an array of files, keeping only those that pass validation.
 * Shows a `toast.error()` for each invalid file.
 */
export const filterValidFiles = (
  files: File[],
  options?: { allowedExtensions?: readonly string[]; allowedMimeTypes?: readonly string[] },
): File[] => {
  return files.filter((file) => {
    try {
      validateFile(file, options);
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Invalid file';
      toast.error(message);
      return false;
    }
  });
};
